import { z } from 'zod'
import { stringSizeMaxMessage } from '@Entities/schemas/work/error-messages'
import { REQUIRED_MESSAGE } from '@Const/error-messages'
import { isPostcodeRequiredCountry } from '@App/common/utils'
import {
    address2Key,
    address3Key,
    address4Key,
    addressFormKey,
    attentionKey,
    cityKey,
    countryKey,
    extendedAddressFormKey,
    postalCodeKey,
    stateKey,
    streetAddressKey,
    unitNumberKey,
} from '@Entities/schemas/common/address-schema-keys'
import { refineCountry, refineRegisterMemberAddress } from '@Entities/schemas/common/address-schema-refinements'
import { buildAddressFormFields, createAddressFormFields } from '@Entities/schemas/common/address-schema-form-fields'

// The base address type used by the user sign up
const baseAddressSchema = z.object({
    [streetAddressKey]: z
        .string({ error: REQUIRED_MESSAGE })
        .max(60, stringSizeMaxMessage('Street Address', 60))
        .min(1, REQUIRED_MESSAGE),
    [unitNumberKey]: z.string().max(10, stringSizeMaxMessage('Unit Number', 10)).optional(),
    [attentionKey]: z.string().max(240).optional(),
    [cityKey]: z
        .string({ error: REQUIRED_MESSAGE })
        .min(1, REQUIRED_MESSAGE)
        .max(60, stringSizeMaxMessage('City / Town', 60)),
    [countryKey]: z.string({ error: REQUIRED_MESSAGE }).min(1, REQUIRED_MESSAGE),
    [stateKey]: z.string().max(60, stringSizeMaxMessage('State / Province', 60)).optional(),
    [postalCodeKey]: z.string().max(60, stringSizeMaxMessage('ZIP / Postal Code', 60)).optional(),
})

// The schema used for the rightsholder forms
const addressSchema = baseAddressSchema.superRefine(refineCountry)

// The schema on the user sign-up form, does not need the attention key for this form.
const userAddressSchema = baseAddressSchema.omit({ [attentionKey]: true }).superRefine(refineCountry)

// The schema used for the public database contact form on the rightsholder page
const registerMemberAddressSchema = z
    .object({
        [streetAddressKey]: z
            .string({ error: REQUIRED_MESSAGE })
            .max(60, stringSizeMaxMessage('Street Address', 60))
            .optional(),
        [unitNumberKey]: z.string().max(10, stringSizeMaxMessage('Unit Number', 10)).optional(),
        [cityKey]: z.string({ error: REQUIRED_MESSAGE }).max(60, stringSizeMaxMessage('City / Town', 60)).optional(),
        [countryKey]: z.string({ error: REQUIRED_MESSAGE }).optional(),
        [stateKey]: z.string().max(60, stringSizeMaxMessage('State / Province', 60)).optional(),
        [postalCodeKey]: z.string().max(60, stringSizeMaxMessage('ZIP / Postal Code', 60)).optional(),
    })
    .superRefine(refineRegisterMemberAddress)

const extendedAddressSchema = z
    .object({
        [cityKey]: z
            .string({ error: REQUIRED_MESSAGE })
            .min(1, REQUIRED_MESSAGE)
            .max(60, stringSizeMaxMessage('City / Town', 60)),
        [address2Key]: z
            .string({ error: REQUIRED_MESSAGE })
            .min(1, REQUIRED_MESSAGE)
            .max(240, stringSizeMaxMessage('Street Address - Line 1', 240)),
        [address3Key]: z.string().max(240, stringSizeMaxMessage('Street Address - Line 2', 240)).optional().nullish(),
        [address4Key]: z.string().max(240, stringSizeMaxMessage('Street Address - Line 3', 240)).optional().nullish(),
        [countryKey]: z.string({ error: REQUIRED_MESSAGE }).min(1, REQUIRED_MESSAGE),
        [stateKey]: z.string().max(60, stringSizeMaxMessage('State / Province', 60)).optional(),
        [postalCodeKey]: z.string().max(60, stringSizeMaxMessage('ZIP / Postal Code', 60)).optional(),
    })
    .superRefine(refineCountry)

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fullAddressSchema = z.union([addressSchema, registerMemberAddressSchema, userAddressSchema])
type RegisterMemberAddressType = z.infer<typeof registerMemberAddressSchema>
type RequiredAddressType = z.infer<typeof addressSchema>
type UserAddressType = z.infer<typeof userAddressSchema>
type AddressType = z.infer<typeof fullAddressSchema>
type ExtendedAddressType = z.infer<typeof extendedAddressSchema>

const { userAddressFormFields, addressFormFields, extendedAddressFormFields } = buildAddressFormFields(
    addressSchema,
    userAddressSchema,
    extendedAddressSchema
)

export {
    addressSchema,
    userAddressSchema,
    addressFormFields,
    registerMemberAddressSchema,
    userAddressFormFields,
    UserAddressType,
    AddressType,
    RegisterMemberAddressType,
    RequiredAddressType,
    addressFormKey,
    extendedAddressSchema,
    extendedAddressFormFields,
    ExtendedAddressType,
    extendedAddressFormKey,
    isPostcodeRequiredCountry,
    createAddressFormFields,
}
